/**
 * EIN Weg vom gespeicherten Rechnungsdatensatz zum Beleg (0055): Vorschau im Portal, Druckansicht, PDF im Browser, PDF beim
 * Versand und im Creator-Portal lesen dieselben eingefrorenen Angaben (issuer_snapshot mit Bank, Steuerhinweis, Logo, Farbe,
 * Register und Vertretung; recipient_snapshot) und bauen daraus mit dieser Funktion dasselbe Modell (InvoicePdfModel).
 * Zwei Renderer mit verschiedenem Inhalt gibt es damit nicht mehr (Befund Einstellungen 7).
 *
 * Regeln:
 * - Aussteller mit Steuernummer und USt-IdNr. (Bezeichnung nach Land: USt-IdNr., UID-Nr., UID/MWST-Nr.), Empfänger nur mit
 *   seiner USt-IdNr. (für Reverse Charge); Kleinunternehmer mit Steuernummer, die USt-IdNr. nur ohne Steuernummer (§ 34a UStDV).
 * - Leistungsmonat nur, wenn der Zeitraum genau einen Kalendermonat umfasst; Sammelrechnung über mehrere Monate: Zeitraum vom
 *   ersten bis zum letzten Monat (Befund Kooperationen 6).
 * - Rechnungen der Agentur nennen das Zahlungsziel in Tagen ab Zugang (Befund 11), Gutschriften „Auszahlung bis“ und das
 *   Auszahlungskonto des Creators (Befund 18), Stornorechnungen keinen Zahlungsaufruf.
 * - Fußzeile mit Firma, Anschrift, Vertretung und Register (Befund 19, Pflichtangaben für Kapitalgesellschaften).
 * - Steuerzeile „MWST“ bei Ausstellern aus der Schweiz und Liechtenstein (Befund 21).
 * - Schweizer QR-Zahlteil (0069): auf Rechnungen der Agentur an Marke oder Creator, wenn das eingefrorene Konto in der
 *   Schweiz oder in Liechtenstein liegt und die Rechnung in CHF oder EUR ist; die Einstellung der Agentur (an, Referenzart,
 *   Kundenidentifikation) friert mit der Bank ein. Nie auf Stornorechnungen und Gutschriften (dort zahlt der Aussteller
 *   des Belegs nicht bzw. die Agentur selbst); Entwürfe nennen nur den Hinweis, weil die Referenz aus der Nummer entsteht.
 */
import { countryName, isEuCountry, normalizeCountry } from "./countries.js";
import type { BankDetails } from "./document-model.js";
import { moneyLocaleFor } from "./money.js";
import { vatBreakdown, type InvoicePdfModel, type PdfImage } from "./invoice-pdf.js";
import { DEFAULT_SWISS_QR_SETTINGS, swissQrBillFor, swissQrSkipText, type SwissQrBill, type SwissQrSettings } from "./swiss-qr-bill.js";
import { isEuVatIdFormat, taxIdLabel, taxModeFromDb } from "./tax.js";
import type { Cents, Currency, IsoDate, Language } from "./types.js";

/** Bank bzw. Auszahlungsweg, wie ihn app.issuer_bank (0055) einfriert */
export interface SnapshotBank {
  method?: "sepa" | "swift" | "paypal" | "wise" | null;
  holder?: string | null;
  iban?: string | null;
  bic?: string | null;
  bank_name?: string | null;
  bank_country?: string | null;
  account?: string | null;
  routing?: string | null;
  email?: string | null;
  /** QR-IBAN des Kontos (Schweiz, 0006/0069) */
  qr_iban?: string | null;
  /** Einstellung QR-Zahlteil der Agentur beim Festschreiben (0069); fehlt sie (ältere Belege), gibt es keinen Zahlteil; ref = eingefrorene, je Agentur eindeutige Referenz */
  qr?: { enabled?: boolean | null; reference?: string | null; prefix?: string | null; ref?: string | null } | null;
}

/** Einstellung QR-Zahlteil aus dem Bankteil eines Belegs (null = keine Angabe, also kein Zahlteil) */
export function qrSettingsOf(b: SnapshotBank | null | undefined): SwissQrSettings | null {
  if (!b?.qr) return null;
  return {
    enabled: b.qr.enabled !== false,
    reference: b.qr.reference === "non" ? "non" : DEFAULT_SWISS_QR_SETTINGS.reference,
    prefix: typeof b.qr.prefix === "string" && b.qr.prefix.trim() ? b.qr.prefix.trim() : null,
  };
}

/**
 * Zahlteil einer Rechnung der Agentur (an Marke oder Creator) aus den eingefrorenen Angaben; dazu der Hinweis für Entwürfe.
 * Stornorechnungen und Gutschriften nie (siehe Kopf).
 */
export function agencyQrBillOf(src: Pick<InvoiceDocSource, "kind" | "number" | "draft" | "currency" | "grossCents" | "selfBilled" | "creditFor" | "issuer" | "recipient" | "bankFallback" | "language">): { bill: SwissQrBill | null; note: string | null } {
  const bank = src.issuer.bank ?? src.bankFallback ?? null;
  const settings = qrSettingsOf(bank);
  if (!isAgencyKind(src.kind) || src.selfBilled || src.creditFor || !settings || !bank) return { bill: null, note: null };
  const input = {
    number: src.number, language: src.language, currency: src.currency, grossCents: Number(src.grossCents),
    creditor: src.issuer, iban: bank.iban ?? null, qrIban: bank.qr_iban ?? null, debtor: src.recipient, settings,
    // eingefrorene, je Agentur eindeutige Referenz (0069); ältere Stände leiten sie aus der Nummer ab
    reference: typeof bank.qr?.ref === "string" ? bank.qr.ref : null,
  };
  if (src.draft) {
    // Entwurf: kein zahlbarer Code; nur sagen, dass er mit der Nummer beim Festschreiben entsteht
    const probe = swissQrBillFor({ ...input, number: src.number ?? "1" });
    return { bill: null, note: probe.bill ? swissQrSkipText("NO_NUMBER") : null };
  }
  return { bill: swissQrBillFor(input).bill, note: null };
}

/** Partei wie in issuer_snapshot bzw. recipient_snapshot (0036, 0039, 0055) */
export interface SnapshotParty {
  name?: string | null;
  street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  vat_id?: string | null;
  tax_number?: string | null;
  email?: string | null;
  representative?: string | null;
  representative_position?: string | null;
  legal_form?: string | null;
  register?: string | null;
  reference?: string | null;
  on_behalf_of?: string | null;
  small_business?: boolean | null;
  logo_path?: string | null;
  accent?: string | null;
  bank?: SnapshotBank | null;
  notice?: string | null;
  /** Gutschrift: die ausstellende Agentur (Logo, Farbe) */
  agency?: { logo_path?: string | null; accent?: string | null; name?: string | null } | null;
}

export interface InvoiceDocLine {
  description: string;
  quantity: number | string | null;
  unitCents: Cents;
  netCents: Cents;
  taxRateBp: number | null;
}

export interface InvoiceDocSource {
  kind: "agency_to_brand" | "agency_to_talent" | "talent_to_agency" | "talent_to_brand";
  number: string | null;
  draft: boolean;
  issueDate: IsoDate | null;
  servicePeriodStart: IsoDate | null;
  servicePeriodEnd: IsoDate | null;
  /** Leistungsmonat 'YYYY-MM' oder 'YYYY-MM-DD' */
  revenueMonth: string | null;
  /** Sammelrechnung: Monate der Rechnung ('YYYY-MM' bzw. Datum), mehr als einer = Zeitraum */
  collectiveMonths?: readonly string[];
  currency: Currency;
  netCents: Cents;
  taxCents: Cents;
  grossCents: Cents;
  taxMode: string | null;
  documentDueDate: IsoDate | null;
  effectiveDueDate?: IsoDate | null;
  issuerDueDate?: IsoDate | null;
  /** Zahlungsziel in Tagen ab Zugang (Agenturrechnungen, 0055) */
  paymentTermDays: number | null;
  language: Language;
  selfBilled: boolean;
  /** paidOutCents (Stornobeleg zur Gutschrift, Entscheidung 16.10.): aus der Gutschrift bereits ausgezahlt; fehlt = unbekannt */
  creditFor: { number: string | null; date: IsoDate | null; paidOutCents?: number | null } | null;
  issuer: SnapshotParty;
  recipient: SnapshotParty;
  lines: readonly InvoiceDocLine[];
  /** Nur für Entwürfe bzw. alte Belege ohne eingefrorene Angaben: aktuelle Bank und aktueller Steuerhinweis */
  bankFallback?: SnapshotBank | null;
  noticeFallback?: string | null;
  accentFallback?: string | null;
  logo?: PdfImage | null;
  /** 0267 (K-296): eingefrorene Umsatzsteuer in Euro bei fremder Rechnungswährung */
  taxEur?: { cents: Cents; rate: number; on: IsoDate; source?: string | null } | null;
}

const clean = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

export const isAgencyKind = (kind: InvoiceDocSource["kind"]) => kind === "agency_to_brand" || kind === "agency_to_talent";

function monthStart(v: string): IsoDate {
  return `${v.slice(0, 7)}-01`;
}
function monthEnd(v: string): IsoDate {
  const y = Number(v.slice(0, 4)), m = Number(v.slice(5, 7));
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

/** Leistung: genau ein Kalendermonat → Monat, sonst Zeitraum (Sammelrechnung: erster bis letzter Monat) */
export function serviceOf(src: Pick<InvoiceDocSource, "servicePeriodStart" | "servicePeriodEnd" | "revenueMonth" | "collectiveMonths">): { month: string | null; period: { from: IsoDate; to: IsoDate } | null } {
  const months = [...new Set((src.collectiveMonths ?? []).map((m) => m.slice(0, 7)))].sort();
  if (months.length > 1) {
    const from = src.servicePeriodStart && src.servicePeriodStart < monthStart(months[0]!) ? src.servicePeriodStart : monthStart(months[0]!);
    const to = src.servicePeriodEnd && src.servicePeriodEnd > monthEnd(months[months.length - 1]!) ? src.servicePeriodEnd : monthEnd(months[months.length - 1]!);
    return { month: null, period: { from, to } };
  }
  const start = src.servicePeriodStart?.slice(0, 10) ?? null;
  const end = src.servicePeriodEnd?.slice(0, 10) ?? start;
  if (start) {
    if (start === monthStart(start) && end === monthEnd(start)) return { month: start.slice(0, 7), period: null };
    return { month: null, period: { from: start, to: end ?? start } };
  }
  if (src.revenueMonth) return { month: src.revenueMonth.slice(0, 7), period: null };
  if (months.length === 1) return { month: months[0]!, period: null };
  return { month: null, period: null };
}

/** Bezeichnung der Steuerzeile nach Land des Ausstellers */
export function taxWordFor(country: string | null | undefined, lang: Language): string | null {
  const c = normalizeCountry(country ?? null);
  if (c === "CH" || c === "LI") return lang === "en" ? "VAT" : "MWST";
  return null;
}

function bankOf(b: SnapshotBank | null | undefined): BankDetails | null {
  if (!b) return null;
  const out: BankDetails = {
    method: b.method ?? null, accountHolder: clean(b.holder), iban: clean(b.iban), bic: clean(b.bic), bankName: clean(b.bank_name),
    bankCountry: clean(b.bank_country), accountNumber: clean(b.account), routingCode: clean(b.routing), email: clean(b.email),
  };
  return out.iban || out.accountNumber || out.email ? out : null;
}

function addressLines(p: SnapshotParty, lang: Language, otherCountry: string | null): string[] {
  const code = normalizeCountry(clean(p.country));
  const lines = [clean(p.street), [clean(p.postal_code), clean(p.city)].filter(Boolean).join(" ") || null];
  if (code && code !== otherCountry) lines.push(countryName(code, lang));
  return lines.filter((x): x is string => !!x);
}

/** Fußzeile: Firma mit Anschrift und Mail, Vertretung und Register (Pflichtangaben auf Geschäftsbriefen) */
export function footerLinesOf(p: SnapshotParty, lang: Language): string[] {
  const out: string[] = [];
  const first = [clean(p.name), [clean(p.street), [clean(p.postal_code), clean(p.city)].filter(Boolean).join(" ")].filter(Boolean).join(", ") || null, clean(p.email)]
    .filter(Boolean).join(" · ");
  if (first) out.push(first);
  const rep = clean(p.representative);
  const pos = clean(p.representative_position);
  // K-404: eingefrorene Angaben mit doppelter Registerart („HRB HRB 98765“, Nummer enthielt die Art schon) einmal ausgeben
  const register = clean(p.register)?.replace(/^([A-Za-zÄÖÜäöü.]+)\s+\1(?=[\s\d])/i, "$1") ?? null;
  const legal = [rep ? `${lang === "en" ? "Represented by" : "Vertreten durch"} ${rep}${pos ? ` (${pos})` : ""}` : null, register].filter(Boolean).join(" · ");
  if (legal) out.push(legal);
  return out;
}

/** Pflichtangaben einer Partei: beim Aussteller Steuernummer und USt-IdNr., beim Empfänger nur die USt-IdNr. */
function fieldsOf(p: SnapshotParty, isIssuer: boolean, lang: Language, smallBusiness: boolean): { label: string; value: string }[] {
  const country = normalizeCountry(clean(p.country));
  const out: { label: string; value: string }[] = [];
  if (isIssuer && clean(p.tax_number)) out.push({ label: lang === "en" ? "Tax number" : "Steuernummer", value: clean(p.tax_number)! });
  // Kleinunternehmer: USt-IdNr. nur, wenn keine Steuernummer da ist; eine der beiden muss auf dem Beleg stehen (§ 34a Satz 1 Nr. 2 UStDV)
  // 0444 (Steuerprüfung L7): Bezeichnung nach dem Land der Partei, eine Nummer aus einem Drittland heißt also nie „USt-IdNr.“;
  // hat eine Partei außerhalb der EU eine USt-IdNr. eines Mitgliedstaats, trägt sie dessen Bezeichnung
  const vatCountry = country && !isEuCountry(country) && isEuVatIdFormat(p.vat_id) ? normalizeCountry(clean(p.vat_id)!.slice(0, 2)) ?? country : country;
  if (clean(p.vat_id) && !(isIssuer && smallBusiness && clean(p.tax_number))) out.push({ label: taxIdLabel(vatCountry, lang), value: clean(p.vat_id)! });
  return out;
}

export function invoicePdfModelFromSource(src: InvoiceDocSource): InvoicePdfModel {
  const lang: Language = src.language === "en" ? "en" : "de";
  const issuerCountry = normalizeCountry(clean(src.issuer.country));
  const recipientCountry = normalizeCountry(clean(src.recipient.country));
  const mode = taxModeFromDb(src.taxMode);
  const smallBusiness = mode === "SMALL_BUSINESS";
  const service = serviceOf(src);
  const rates = [...new Set(src.lines.map((l) => Number(l.taxRateBp ?? 0)).filter((r) => r > 0))];
  const agency = isAgencyKind(src.kind);
  const credit = src.creditFor ? { forNumber: src.creditFor.number ?? "", forDate: src.creditFor.date, paidOutCents: src.creditFor.paidOutCents ?? null } : null;
  const term = agency && !credit && typeof src.paymentTermDays === "number" ? src.paymentTermDays : null;
  const recipientRef = [clean(src.recipient.reference), clean(src.recipient.on_behalf_of) ? `${lang === "en" ? "for" : "für"} ${clean(src.recipient.on_behalf_of)}` : null]
    .filter(Boolean).join(" · ");
  return {
    language: lang,
    locale: moneyLocaleFor(lang, issuerCountry),
    currency: src.currency,
    number: src.number ?? (lang === "en" ? "DRAFT" : "ENTWURF"),
    draft: src.draft,
    invoiceDate: src.issueDate?.slice(0, 10) ?? null,
    serviceMonth: service.month,
    service: service.period,
    dueDate: (src.documentDueDate ?? src.effectiveDueDate ?? src.issuerDueDate ?? null)?.slice(0, 10) ?? null,
    paymentTermDays: term,
    issuer: { name: clean(src.issuer.name) ?? "–", addressLines: addressLines(src.issuer, lang, recipientCountry), fields: fieldsOf(src.issuer, true, lang, smallBusiness) },
    recipient: { name: clean(src.recipient.name) ?? "–", addressLines: addressLines(src.recipient, lang, issuerCountry), fields: fieldsOf(src.recipient, false, lang, false) },
    lines: src.lines.map((l) => ({ description: l.description, quantity: Number(l.quantity ?? 1), unitPriceCents: Number(l.unitCents), totalCents: Number(l.netCents) })),
    totals: {
      netCents: Number(src.netCents), vatCents: Number(src.taxCents), grossCents: Number(src.grossCents),
      ratePercent: rates.length === 1 ? rates[0]! / 100 : rates.length === 0 && Number(src.taxCents) === 0 ? 0 : null,
      // L2: mehrere Sätze (eingelesene Belege, Creator-Rechnungen mit Positionssätzen): je Satz Entgelt und Steuer
      byRate: vatBreakdown(src.lines.map((l) => ({ netCents: Number(l.netCents), rateBp: l.taxRateBp })), Number(src.taxCents)),
      // K-280: Aussteller in CH/LI ohne MWST-Registrierung zeigen keine Zeile „MWST 0.00“; ebenso Reverse Charge und Leistung
      // ins Ausland (Inhaber 16.10.2026): dort sagt der Steuerhinweis alles
      hideVat: (issuerCountry === "CH" || issuerCountry === "LI") && Number(src.taxCents) === 0
        && (smallBusiness || mode === "REVERSE_CHARGE_EU" || mode === "REVERSE_CHARGE_DOMESTIC" || mode === "NOT_TAXABLE_ABROAD"),
    },
    taxNotice: clean(src.issuer.notice) ?? clean(src.noticeFallback),
    bank: credit ? null : bankOf(src.issuer.bank ?? src.bankFallback),
    ...(() => { const q = agencyQrBillOf({ ...src, language: lang }); return { qrBill: q.bill, qrBillNote: q.note }; })(),
    reference: recipientRef || null,
    footerLines: footerLinesOf(src.issuer, lang),
    creditNote: credit,
    selfBilling: src.selfBilled,
    lateCosts: mode === "NOT_TAXABLE_DAMAGES",
    taxLabel: taxWordFor(issuerCountry, lang),
    accent: clean(src.issuer.accent) ?? clean(src.issuer.agency?.accent) ?? clean(src.accentFallback),
    logo: src.logo ?? null,
    taxEur: src.taxEur ?? null,
  };
}

/** Pfad des Logos, das auf den Beleg gehört: Agentur als Ausstellerin bzw. als Erstellerin der Gutschrift */
export function invoiceLogoPath(issuer: SnapshotParty | null | undefined): string | null {
  return clean(issuer?.logo_path) ?? clean(issuer?.agency?.logo_path);
}
